import classNames from "classnames"
import { storyblokEditable } from "gatsby-source-storyblok"

import CountDownBanner from "./CountDownBanner"
import IconBanner from "./IconBanner"
import Section from "../../..//elements/V2/Section"
import { Color } from "../../../../constants/V2/color"
import LayoutItem from "../../Layouts/LayoutItem"

import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import { trackSegmentEvent } from "@utils/analytics"
import { usePageLayoutState } from "@utils/layout"
import { getBackgroundColorClass, getBorderColorClass } from "@utils/V2/color"
import React, { useEffect, useState } from "react"

export type ToastNotificationsBaseProps = {
  ctaText: string
  ctaLink: string | YocoCom.YocoLink | undefined
  ctaColor?: Color
  textColor?: Color
  textAlignment?: string
  backgroundColor: Color
  secondaryBackgroundColor?: Color
  mobileGridOrder?: number
  isSiteWideBanner?: boolean
  isAnimated?: boolean
  onClick?: VoidFunction
}

export type ToastNotificationsCountDownProps = {
  text?: string
  timerLabel: string
  ctaTextColor?: Color
  timerTextColor?: Color
  timerTargetDate: string
  component: "toastNotificationCountDownBanner"
}

export type ToastNotificationsIconProps = {
  icon?: string
  iconAlt?: string
  text: string
  mobileText?: string
  component: "toastNotificationIconBanner"
}

type Props = ToastNotificationsBaseProps &
  (ToastNotificationsCountDownProps | ToastNotificationsIconProps) &
  ReturnType<typeof storyblokEditable>

const ToastNotification = ({
  text,
  ctaText,
  ctaLink,
  ctaColor,
  textColor,
  textAlignment,
  backgroundColor,
  secondaryBackgroundColor,
  mobileGridOrder,
  isSiteWideBanner = false,
  isAnimated = false,
  ...rest
}: Props) => {
  const [isClosed, setIsClosed] = useState(false)
  const { setHasSiteBanner: setSiteBanner } = usePageLayoutState()

  useEffect(() => {
    if (window) {
      setSiteBanner(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storyblokProps = {
    "data-blok-c": rest["data-blok-c"],
    "data-blok-uid": rest["data-blok-uid"],
  }

  const siteWideBannerTrackingEvent = isSiteWideBanner
    ? () => {
        trackSegmentEvent("site_wide_banner_click", {
          anchorText: ctaText,
          bannerText: text,
          path: typeof ctaLink === "string" ? ctaLink : ctaLink?.url,
        })
      }
    : undefined

  const glowClasses = classNames(
    "pointer-events-none absolute left-1/2 top-full mt-[min(1.46vw,21px)] h-[min(56.1vw,808px)] w-[117vw] -translate-x-1/2 rounded-[50%] border-[min(3.54vw,51px)] blur-[min(3.26vw,47px)]",
    secondaryBackgroundColor && getBorderColorClass(secondaryBackgroundColor)
  )

  return (
    <LayoutItem
      as={Section}
      mobileGridOrder={mobileGridOrder}
      {...storyblokProps}
      className={classNames(
        "relative flex h-[50px] items-center gap-10 overflow-hidden",
        getBackgroundColorClass(backgroundColor),
        { hidden: isClosed }
      )}
    >
      {!!secondaryBackgroundColor && (
        <>
          <div aria-hidden className={glowClasses} />
          {isAnimated && (
            <div
              aria-hidden
              className={classNames(glowClasses, "animate-glowRipple")}
            />
          )}
        </>
      )}

      <div className="relative flex w-full items-center gap-10">
        {rest.component === "toastNotificationCountDownBanner" ? (
          <CountDownBanner
            text={text}
            ctaText={ctaText}
            ctaLink={ctaLink}
            ctaColor={ctaColor}
            textColor={textColor}
            timerLabel={rest.timerLabel}
            backgroundColor={backgroundColor}
            timerTextColor={rest.timerTextColor}
            timerTargetDate={rest.timerTargetDate}
            onClick={siteWideBannerTrackingEvent}
          />
        ) : (
          <IconBanner
            text={text!}
            mobileText={rest.mobileText}
            icon={rest.icon}
            ctaText={ctaText}
            ctaLink={ctaLink}
            ctaColor={ctaColor}
            textColor={textColor}
            textAlignment={textAlignment}
            iconAlt={rest.iconAlt}
            backgroundColor={backgroundColor}
            onClick={siteWideBannerTrackingEvent}
          />
        )}
      </div>

      <div className="relative h-24 w-24 cursor-pointer">
        <CloseIcon
          width="24px"
          height="24px"
          hideBorder
          strokeWidth="2"
          strokeColor={textColor}
          onClick={() => {
            setIsClosed(true)
            setSiteBanner(false)
          }}
        />
      </div>
    </LayoutItem>
  )
}

export default ToastNotification
